import { ApPayee } from '@/components/ApPayee';
import { KeywordCont } from '@/components/KeywordCont';
import { PicIdIn } from '@/components/PicIdIn';
import { ErrorBoundary } from '@/components/Rollbar';
import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import { UpdatedAt } from '@/components/UpdatedAt';
import { TReceivedInvoiceCondition } from '@/context/services/invoice/type';
import { AmountRange } from '@/features/invoices/components/List/Search/AmountRange';
import { DueDate } from '@/features/invoices/components/List/Search/DueDate';
import { useApPayeeInitialData } from '@/features/invoices/components/List/Search/hooks';
import { OperationStatus } from '@/features/invoices/components/List/Search/OperationStatus';
import { UploadType } from '@/features/invoices/components/List/Search/UploadType';
import { useTranslation } from '@/i18n';
import {
  ButtonV2,
  Form,
  FormSelectProps,
  SearchConditions,
  useNotification,
} from '@moneyforward/ap-frontend-components';
import { Col, Row } from 'antd';
import classnames from 'classnames/bind';
import {
  Dispatch,
  FC,
  SetStateAction,
  Suspense,
  memo,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { FieldValues, useForm, useFormState } from 'react-hook-form';
import styles from './Search.module.scss';

const cx = classnames.bind(styles);

type ErrorFallbackProps = {
  error: Error | null;
};

const ErrorFallback: FC<ErrorFallbackProps> = ({ error }) => {
  const notify = useNotification();
  const { t } = useTranslation();
  useEffect(() => {
    notify.error({
      title: error?.message ?? t('system_error'),
      opts: {
        duration: 3000,
        style: {
          position: 'fixed',
        },
      },
    });
  }, [error, notify, t]);
  return <></>;
};
ErrorFallback.displayName = 'ErrorFallback';

export type Props = {
  defaultValues: TReceivedInvoiceCondition;
  onSearch: (queryParams: TReceivedInvoiceCondition) => void;
  setIsTriggeredSearch: Dispatch<SetStateAction<boolean>>;
  isPolling: boolean;
};

type SelectProps<TFieldValues extends FieldValues> = Omit<
  FormSelectProps<TFieldValues>['selectProps'],
  'options'
>;

const InnerSearch: FC<Props> = memo(
  ({ defaultValues, onSearch, setIsTriggeredSearch, isPolling }) => {
    const { t } = useTranslation();
    const methods = useForm<TReceivedInvoiceCondition>({
      defaultValues,
      mode: 'all',
    });

    // Get form state to check for errors
    const { errors } = useFormState({
      control: methods.control,
    });

    useEffect(() => {
      methods.reset(defaultValues);
    }, [defaultValues, methods]);
    const payeeOptions = useApPayeeInitialData(
      defaultValues.ap_payee_id_in ?? []
    );
    const handleSubmit = useCallback(
      (data: TReceivedInvoiceCondition) => {
        onSearch(data);
        if (isPolling) {
          setIsTriggeredSearch(true);
        }
      },
      [isPolling, onSearch, setIsTriggeredSearch]
    );

    const picLabelProps = useMemo(
      () => ({
        text: t('received_invoice_pics'),
      }),
      [t]
    );

    const apPayeeSelectProps: SelectProps<FieldValues> = useMemo(
      () => ({
        mode: 'multiple',
        maxTagCount: 1,
        className: 'apPayee',
        inlineOptions: true,
      }),
      []
    );

    const keywordLabelProps = useMemo(
      () => ({
        text: t('received_invoice_key_word'),
      }),
      [t]
    );

    const keywordInputProps = useMemo(
      () => ({
        placeholder: t('received_invoice_key_word_placeholder'),
      }),
      [t]
    );

    // Custom validation rules for the search field
    const searchableNumberOrFilenameValidationRules = useMemo(
      () => ({
        maxLength: {
          value: 100,
          message: t('common.validation.max_length', { maxLength: 100 }),
        },
        pattern: {
          value: /^(?!\s*$).+/,
          message: t('common.validation.required'),
        },
      }),
      [t]
    );

    const _label = useMemo(
      () => ({
        text: t('label_ap_payee'),
      }),
      [t]
    );

    // Check if there are any validation errors
    const hasErrors = Object.keys(errors).length > 0;

    return (
      <div className={cx(styles['searchCondition'])}>
        <SearchConditions>
          <Form<TReceivedInvoiceCondition>
            formMethod={methods}
            onSubmit={handleSubmit}
            testId='searchForm'
            className={cx(styles['searchForm'])}
          >
            <div className={cx(styles['dueDateAmountLayout'])}>
              <Row gutter={8} align='bottom'>
                <Col span={6}>
                  <KeywordCont
                    control={methods.control}
                    name='searchable_number_or_filename_cont'
                    labelProps={keywordLabelProps}
                    inputProps={keywordInputProps}
                    rules={searchableNumberOrFilenameValidationRules}
                  />
                </Col>
                <Col span={6}>
                  <ApPayee
                    label={_label}
                    control={methods.control}
                    name='ap_payee_id_in'
                    inputId='select-ap-payee'
                    testId='ap-payee-select'
                    selectProps={apPayeeSelectProps}
                    defaultOptions={payeeOptions}
                    include_inactive
                  />
                </Col>
                {/* 368px vs 280px in the design — 8/6 keeps that ratio. */}
                <Col span={8}>
                  <AmountRange control={methods.control} />
                </Col>
                <Col span={4}>
                  <PicIdIn<TReceivedInvoiceCondition>
                    control={methods.control}
                    name='pic_id_in'
                    labelProps={picLabelProps}
                  />
                </Col>
              </Row>
              <Row gutter={8} align='bottom'>
                <Col span={6}>
                  <DueDate control={methods.control} name='due_date' />
                </Col>
                <Col span={6}>
                  <UpdatedAt control={methods.control} name='uploaded_date' />
                </Col>
                <Col span={4}>
                  <UploadType control={methods.control} name='upload_type_in' />
                </Col>
                <Col span={4}>
                  <OperationStatus
                    control={methods.control}
                    name='operation_status_in'
                  />
                </Col>
                <Col span={4} className={cx(styles['searchButtonCol'])}>
                  <ButtonV2
                    isSecondary
                    size='sm'
                    type='submit'
                    testId='search-btn'
                    isDisabled={hasErrors}
                  >
                    {t('search')}
                  </ButtonV2>
                </Col>
              </Row>
            </div>
          </Form>
        </SearchConditions>
      </div>
    );
  }
);

InnerSearch.displayName = 'InnerSearch';

export const ReceivedInvoiceSearch: FC<Props> = memo((props) => {
  return (
    <Suspense fallback={<SuspenseLoading />}>
      <ErrorBoundary fallback={({ error }) => <ErrorFallback error={error} />}>
        <InnerSearch {...props} />
      </ErrorBoundary>
    </Suspense>
  );
});
ReceivedInvoiceSearch.displayName = 'ReceivedInvoiceSearch';
